import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Card, Row, Col, Spinner, Alert, Button, Table } from "react-bootstrap";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import api from "api";
import "./UsersDashboard.css";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { createEsriImageryTileLayer } from "components/maps/esriTileLayer";
import PageHeader from "components/layout/page-header/PageHeader";
import UserTimelineModal from "pages/admin/UserTimelineModal";
import TrialInsightsPanel from "pages/admin/TrialInsightsPanel";

// GeographicMap must live OUTSIDE UsersDashboard so its reference stays stable
// across parent re-renders (opening timeline modal, etc.). Defining it inside
// the parent causes React to unmount/remount the map on every state change,
// which triggers map.remove() mid-zoom-animation → _leaflet_pos crash.
function GeographicMap({ locations }) {
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markersLayerRef = React.useRef(null);

  // Inicializa o mapa uma vez
  React.useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [-14.235, -51.9253],
      zoom: 3,
      minZoom: 2,
      maxZoom: 10,
      worldCopyJump: true
    });

    createEsriImageryTileLayer({ maxZoom: 19 }).addTo(map);

    mapInstanceRef.current = map;
    markersLayerRef.current = L.layerGroup().addTo(map);

    const brazilBounds = L.latLngBounds(
      L.latLng(-33.8689, -73.9831),
      L.latLng(5.2718, -34.7931)
    );
    map.fitBounds(brazilBounds, { animate: false });

    const invalidateTimer = setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 150);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      },
      { threshold: 0.1, rootMargin: "50px" }
    );
    observer.observe(mapRef.current);

    return () => {
      clearTimeout(invalidateTimer);
      observer.disconnect();
      map.stop(); // cancel any ongoing animation before removing
      map.remove();
      mapInstanceRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  // Atualiza marcadores quando locations mudar
  React.useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();

    if (!locations || locations.length === 0) return;

    const validLocations = locations.filter(
      l => l.latitude != null && l.longitude != null
    );
    if (!validLocations.length) return;

    const maxCount =
      Math.max(...validLocations.map(l => l.count || 0)) || 1;

    const latLngs = [];

    validLocations.forEach(loc => {
      const count = loc.count || 0;
      const intensity = count / maxCount;

      const radius = 6 + intensity * 10;
      const color =
        intensity > 0.66
          ? "#ef4444"
          : intensity > 0.33
          ? "#f97316"
          : "#22c55e";

      const circle = L.circleMarker([loc.latitude, loc.longitude], {
        radius,
        color,
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.65
      }).bindTooltip(() => {
        const title =
          loc.city && loc.city !== "Desconhecido" ? loc.city : "Localização";
        const countryPart = loc.country ? " — " + loc.country : "";
        const baseLine = `<strong>${title}${countryPart}</strong><br/>Sessões: ${
          loc.count
        }`;

        // Se o backend enviar usernames distintos para essa localização,
        // mostramos uma prévia dos nomes no tooltip.
        const usernames = Array.isArray(loc.usernames)
          ? loc.usernames.filter(Boolean)
          : [];
        if (!usernames.length) {
          return baseLine;
        }

        const maxShown = 5;
        const shown = usernames.slice(0, maxShown).join(", ");
        const suffix = usernames.length > maxShown ? "…" : "";

        return `${baseLine}<br/><span style="font-size:11px;">Usuários: ${shown}${suffix}</span>`;
      }, { direction: "top", offset: [0, -4], opacity: 0.9 });

      circle.addTo(layer);
      latLngs.push([loc.latitude, loc.longitude]);
    });

    if (latLngs.length) {
      const bounds = L.latLngBounds(latLngs);
      map.fitBounds(bounds.pad(0.2), { maxZoom: 6, animate: false });
    }

    setTimeout(() => {
      if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
    }, 50);
  }, [locations]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", minHeight: "350px" }}>
      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "100%",
          minHeight: "350px",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(15,23,42,0.08)"
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "rgba(255, 255, 255, 0.96)",
          padding: "10px",
          borderRadius: "6px",
          fontSize: "12px",
          boxShadow: "0 2px 6px rgba(15,23,42,0.15)"
        }}
      >
        <div style={{ marginBottom: "6px", fontWeight: "bold" }}>
          Intensidade de sessões
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "#ef4444"
            }}
          />
          <span style={{ fontSize: 11 }}>Mais sessões</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "#22c55e"
            }}
          />
          <span style={{ fontSize: 11 }}>Menos sessões</span>
        </div>
      </div>
      {locations && locations.length > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            left: "10px",
            background: "rgba(255, 255, 255, 0.96)",
            padding: "8px",
            borderRadius: "4px",
            fontSize: "11px",
            boxShadow: "0 1px 3px rgba(15,23,42,0.1)"
          }}
        >
          <strong>{locations.length}</strong> localizações com sessões nos{" "}
          últimos 30 dias
        </div>
      )}
    </div>
  );
}

function UsersDashboard({ token, embedded, trialFilterKey = null, trialFilterNonce = 0 }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [growthRange, setGrowthRange] = useState(30); // dias: 7, 30, 90
  const [growthMode, setGrowthMode] = useState("daily"); // 'daily' ou 'cumulative'
  const [geoRange, setGeoRange] = useState(30); // 7, 30 ou 0 (base completa)

  // Filtro de usuário nas últimas atividades
  const [activityUserFilter, setActivityUserFilter] = useState("");

  const [timelineUser, setTimelineUser] = useState(null);

  useEffect(() => {
    if (token) {
      fetchDashboardStats();
    }
  }, [token, geoRange]);

  const openUserTimeline = (userId, username) => {
    setTimelineUser({ id: userId, username: username || `Usuário #${userId}` });
  };

  const closeTimelineModal = () => {
    setTimelineUser(null);
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get("/users/dashboard-stats", {
        headers: { Authorization: `Bearer ${token}` },
        params: { geo_days: geoRange }
      });

      setStats(response.data);
    } catch (error) {
      setError("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  const getGrowthChartData = () => {
    if (!stats?.daily_registrations) {
      return { labels: [], datasets: [] };
    }

    const sortedData = [...stats.daily_registrations].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const days = growthRange;
    const today = new Date();
    const labels = [];
    const values = [];

    let cumulative = 0;

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const iso = date.toISOString().split("T")[0];
      const formattedLabel = date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit"
      });
      labels.push(formattedLabel);

      const match = sortedData.find(item => item.date === iso);
      const dailyValue = match ? match.new_users : 0;
      if (growthMode === "cumulative") {
        cumulative += dailyValue;
        values.push(cumulative);
      } else {
        values.push(dailyValue);
      }
    }

    return {
      labels,
      datasets: [
        {
          label:
            growthMode === "cumulative"
              ? "Usuários acumulados"
              : "Novos usuários por dia",
          data: values,
          borderColor:
            growthMode === "cumulative"
              ? "rgb(59, 130, 246)"
              : "rgb(75, 192, 192)",
          backgroundColor:
            growthMode === "cumulative"
              ? "rgba(59, 130, 246, 0.18)"
              : "rgba(75, 192, 192, 0.18)",
          tension: 0.35,
          fill: true,
          pointBackgroundColor:
            growthMode === "cumulative"
              ? "rgb(59, 130, 246)"
              : "rgb(75, 192, 192)",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5
        }
      ]
    };
  };

  const growthChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#333",
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(context) {
            return `Data: ${context[0].label}`;
          },
          label: function(context) {
            const prefix =
              growthMode === "cumulative"
                ? "Usuários acumulados"
                : "Novos usuários";
            return `${prefix}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: "category",
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          stepSize: 1
        }
      }
    },
    interaction: {
      intersect: false,
      mode: "index"
    }
  };

  const getSessionsChartData = () => {
    if (!stats?.daily_sessions) return { labels: [], datasets: [] };

    const labels = stats.daily_sessions.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit"
      });
    });

    const data = stats.daily_sessions.map(item => item.sessions);

    return {
      labels,
      datasets: [
        {
          label: "Sessões",
          data: data,
          backgroundColor: "rgba(54, 162, 235, 0.8)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false
        }
      ]
    };
  };

  const sessionsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#333",
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: "Atividade de Sessões (Últimos 30 dias)",
        color: "#333",
        font: {
          size: 16,
          weight: "bold"
        }
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Sessões: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          stepSize: 1
        }
      }
    },
    interaction: {
      intersect: false,
      mode: "index"
    }
  };

  const getSessionDurationChartData = () => {
    if (!stats?.session_duration_distribution)
      return { labels: [], datasets: [] };

    const labels = stats.session_duration_distribution.map(item => item.range);
    const data = stats.session_duration_distribution.map(item => item.count);

    return {
      labels,
      datasets: [
        {
          label: "Sessões",
          data: data,
          backgroundColor: [
            "rgba(255, 99, 132, 0.8)", // Vermelho
            "rgba(255, 159, 64, 0.8)", // Laranja
            "rgba(255, 205, 86, 0.8)", // Amarelo
            "rgba(75, 192, 192, 0.8)", // Verde-azulado
            "rgba(54, 162, 235, 0.8)", // Azul
            "rgba(153, 102, 255, 0.8)", // Roxo
            "rgba(201, 203, 207, 0.8)", // Cinza
            "rgba(255, 99, 132, 0.8)" // Vermelho (repetido)
          ],
          borderColor: [
            "rgba(255, 99, 132, 1)",
            "rgba(255, 159, 64, 1)",
            "rgba(255, 205, 86, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(54, 162, 235, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(201, 203, 207, 1)",
            "rgba(255, 99, 132, 1)"
          ],
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false
        }
      ]
    };
  };

  const sessionDurationChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#333",
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: "Distribuição de Duração de Sessões",
        color: "#333",
        font: {
          size: 16,
          weight: "bold"
        }
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Sessões: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          stepSize: 1
        }
      }
    },
    interaction: {
      intersect: false,
      mode: "index"
    }
  };

  const getDeviceBrowserChartData = () => {
    if (!stats?.device_browser_distribution)
      return { labels: [], datasets: [] };

    // Criar labels combinando device_type e browser
    const labels = stats.device_browser_distribution.map(
      item => `${item.device_type} - ${item.browser}`
    );

    const data = stats.device_browser_distribution.map(item => item.count);

    // Cores para diferentes combinações
    const colors = [
      "#FF6384",
      "#36A2EB",
      "#FFCE56",
      "#4BC0C0",
      "#9966FF",
      "#FF9F40",
      "#FF6384",
      "#C9CBCF",
      "#4BC0C0",
      "#FF6384",
      "#36A2EB",
      "#FFCE56"
    ];

    return {
      labels,
      datasets: [
        {
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: colors
            .slice(0, labels.length)
            .map(color => color.replace("0.8", "1")),
          borderWidth: 2,
          hoverBackgroundColor: colors
            .slice(0, labels.length)
            .map(color => color.replace("0.8", "0.9")),
          hoverBorderColor: colors
            .slice(0, labels.length)
            .map(color => color.replace("0.8", "1"))
        }
      ]
    };
  };

  const deviceBrowserChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          color: "#333",
          font: {
            size: 11
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle"
        }
      },
      title: {
        display: true,
        text: "Dispositivos & Navegadores",
        color: "#333",
        font: {
          size: 16,
          weight: "bold"
        }
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#36A2EB",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.label;
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    cutout: "50%",
    radius: "90%"
  };

  const getUserActivityChartData = () => {
    if (!stats?.user_activity_distribution) return { labels: [], datasets: [] };

    // Mapear activity_type para labels mais amigáveis
    const activityLabels = {
      page_view: "Visualização de Página",
      login: "Login",
      logout: "Logout",
      project_upload: "Upload de Projeto",
      project_list: "Lista de Projetos",
      project_delete: "Exclusão de Projeto",
      report_view: "Visualização de Relatório",
      assistant_use: "Uso do Assistente",
      plan_view: "Visualização de Plano",
      payment_initiated: "Início de Pagamento",
      signup: "Cadastro",
      user_activity: "Atividade Geral"
    };

    const labels = stats.user_activity_distribution.map(
      item => activityLabels[item.activity_type] || item.activity_type
    );

    const data = stats.user_activity_distribution.map(item => item.count);

    return {
      labels,
      datasets: [
        {
          label: "Ações",
          data: data,
          backgroundColor: "rgba(255, 99, 132, 0.8)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false
        }
      ]
    };
  };

  const userActivityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y", // Barras horizontais
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: "Ações de Usuário",
        color: "#333",
        font: {
          size: 16,
          weight: "bold"
        }
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            return `Ações: ${context.parsed.x}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)"
        },
        ticks: {
          color: "#666",
          stepSize: 1
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          color: "#666",
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: "index"
    }
  };


  if (!token) {
    return (
      <div className="users-dashboard">
        {!embedded && (
          <PageHeader
            title="Dashboard de Usuários"
            subtitle="Monitoramento e análise de usuários do sistema."
            accent="Administração"
          />
        )}
        <Alert variant="warning">
          <Alert.Heading>Acesso Negado</Alert.Heading>
          <p>Você precisa estar logado para acessar este dashboard.</p>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="users-dashboard">
        {!embedded && (
          <PageHeader
            title="Dashboard de Usuários"
            subtitle="Monitoramento e análise de usuários do sistema."
            accent="Administração"
          />
        )}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="users-dashboard">
        {!embedded && (
          <PageHeader
            title="Dashboard de Usuários"
            subtitle="Monitoramento e análise de usuários do sistema."
            accent="Administração"
          />
        )}
        <Alert variant="danger">
          <Alert.Heading>Erro ao carregar dados</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className="users-dashboard"
      style={{ minHeight: "100vh", overflowY: "auto" }}
    >
      {!embedded && (
        <PageHeader
          title="Dashboard de Usuários"
          subtitle="Monitoramento e análise de usuários do sistema."
          accent="Administração"
        />
      )}

      <Row className={embedded ? "mt-2" : "mt-6"}>
        <Col xs={12} className="mb-3">
          <div id="admin-trial-insights-users">
            <TrialInsightsPanel
              token={token}
              variant="panel"
              syncFilterKey={trialFilterKey}
              syncNonce={trialFilterNonce}
              onUserClick={u => openUserTimeline(u.id, u.username)}
            />
          </div>
        </Col>

        {/* Total de Usuários Cadastrados */}
        <Col md={3} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                document
                  .getElementById("admin-trial-insights-users")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            style={{ cursor: "pointer" }}
            onClick={() =>
              document
                .getElementById("admin-trial-insights-users")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-users text-primary"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-primary mb-2">
                {stats?.overview?.total_users?.toLocaleString() || "0"}
              </h4>
              <p className="text-muted mb-0">Total cadastrados · clique → trials</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Usuários Ativos (últimos 30 dias) */}
        <Col md={3} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() =>
              document
                .getElementById("users-growth-chart")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                document
                  .getElementById("users-growth-chart")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-user-check text-success"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-success mb-2">
                {stats?.overview?.active_users_30_days?.toLocaleString() || "0"}
              </h4>
              <p className="text-muted mb-0">Ativos 30d · clique → crescimento</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Novos Cadastros (hoje) */}
        <Col md={3} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() =>
              document
                .getElementById("users-growth-chart")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                document
                  .getElementById("users-growth-chart")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-user-plus text-info"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-info mb-2">
                {stats?.overview?.new_users_today?.toLocaleString() || "0"}
              </h4>
              <p className="text-muted mb-0">Novos hoje · clique → gráfico</p>
            </Card.Body>
          </Card>
        </Col>

        {/* Média de Sessões por Usuário */}
        <Col md={3} className="mb-4">
          <Card
            className="metric-card h-100"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() =>
              document
                .getElementById("users-sessions-chart")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                document
                  .getElementById("users-sessions-chart")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
          >
            <Card.Body className="text-center">
              <div className="mb-3">
                <i
                  className="fas fa-chart-line text-warning"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="text-warning mb-2">
                {stats?.overview?.avg_sessions_per_user?.toFixed(1) || "0"}
              </h4>
              <p className="text-muted mb-0">Média sessões · clique → atividade</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Crescimento de Cadastros */}
      <Row className="mt-4" id="users-growth-chart">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-line text-info me-2"></i>
                Crescimento de Cadastros
              </h5>
              <div className="d-flex flex-wrap align-items-center justify-content-between mt-2">
                <small className="text-muted">
                  Evolução de novos usuários em diferentes janelas de tempo
                </small>
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-sm-0">
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      className={
                        "btn " +
                        (growthRange === 7 ? "btn-primary" : "btn-outline-primary")
                      }
                      onClick={() => setGrowthRange(7)}
                    >
                      7d
                    </button>
                    <button
                      type="button"
                      className={
                        "btn " +
                        (growthRange === 30 ? "btn-primary" : "btn-outline-primary")
                      }
                      onClick={() => setGrowthRange(30)}
                    >
                      30d
                    </button>
                    <button
                      type="button"
                      className={
                        "btn " +
                        (growthRange === 90 ? "btn-primary" : "btn-outline-primary")
                      }
                      onClick={() => setGrowthRange(90)}
                    >
                      90d
                    </button>
                  </div>
                  <div className="btn-group btn-group-sm ms-2" role="group">
                    <button
                      type="button"
                      className={
                        "btn " +
                        (growthMode === "daily"
                          ? "btn-outline-secondary active"
                          : "btn-outline-secondary")
                      }
                      onClick={() => setGrowthMode("daily")}
                    >
                      Diário
                    </button>
                    <button
                      type="button"
                      className={
                        "btn " +
                        (growthMode === "cumulative"
                          ? "btn-outline-secondary active"
                          : "btn-outline-secondary")
                      }
                      onClick={() => setGrowthMode("cumulative")}
                    >
                      Acumulado
                    </button>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "400px" }}>
                <Line
                  data={getGrowthChartData()}
                  options={growthChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Distribuição Geográfica */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <i className="fas fa-globe text-primary me-2"></i>
                    Distribuição Geográfica
                  </h5>
                  <small className="text-muted">
                    Sessões por localização (latitude/longitude)
                  </small>
                </div>
                <div className="btn-group btn-group-sm" role="group" aria-label="Intervalo geográfico">
                  <button
                    type="button"
                    className={
                      "btn " +
                      (geoRange === 7 ? "btn-outline-secondary active" : "btn-outline-secondary")
                    }
                    onClick={() => setGeoRange(7)}
                  >
                    7 dias
                  </button>
                  <button
                    type="button"
                    className={
                      "btn " +
                      (geoRange === 30 ? "btn-outline-secondary active" : "btn-outline-secondary")
                    }
                    onClick={() => setGeoRange(30)}
                  >
                    30 dias
                  </button>
                  <button
                    type="button"
                    className={
                      "btn " +
                      (geoRange === 0 ? "btn-outline-secondary active" : "btn-outline-secondary")
                    }
                    onClick={() => setGeoRange(0)}
                  >
                    Base completa
                  </button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "400px" }}>
                <GeographicMap
                  locations={stats?.geographic_distribution?.locations || []}
                />
              </div>
              {(!stats?.geographic_distribution?.locations ||
                stats.geographic_distribution.locations.length === 0) && (
                <div className="text-center mt-3">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Carregando dados geográficos... Se não aparecerem
                    marcadores, pode ser que não haja sessões com coordenadas
                    válidas nos últimos 30 dias.
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Atividade de Sessões */}
      <Row className="mt-4" id="users-sessions-chart">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-calendar-alt text-warning me-2"></i>
                Atividade de Sessões
              </h5>
              <small className="text-muted">Número de sessões por dia</small>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "400px" }}>
                <Bar
                  data={getSessionsChartData()}
                  options={sessionsChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Duração de Sessões */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-clock text-secondary me-2"></i>
                Duração de Sessões
              </h5>
              <small className="text-muted">
                Distribuição de duração das sessões
              </small>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "400px" }}>
                <Bar
                  data={getSessionDurationChartData()}
                  options={sessionDurationChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Dispositivos & Navegadores */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-laptop text-primary me-2"></i>
                Dispositivos & Navegadores
              </h5>
              <small className="text-muted">
                Proporção por tipo de dispositivo e navegador
              </small>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "400px" }}>
                <Doughnut
                  data={getDeviceBrowserChartData()}
                  options={deviceBrowserChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Ações de Usuário */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-bar text-success me-2"></i>
                Ações de Usuário
              </h5>
              <small className="text-muted">
                Contagem por tipo de atividade
              </small>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "400px" }}>
                <Bar
                  data={getUserActivityChartData()}
                  options={userActivityChartOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Últimas atividades — clique no nome para ver timeline do usuário */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="h-100">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">
                    <i className="fas fa-history text-info me-2"></i>
                    Últimas atividades
                  </h5>
                  <small className="text-muted">
                    Clique no nome do usuário para ver a timeline completa
                  </small>
                </div>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: "200px" }}
                  value={activityUserFilter}
                  onChange={e => setActivityUserFilter(e.target.value)}
                >
                  <option value="">Todos os usuários</option>
                  {(() => {
                    const seen = new Map();
                    (stats?.recent_activities || []).forEach(act => {
                      const key = act.user_id ? String(act.user_id) : "";
                      if (key && !seen.has(key)) {
                        seen.set(key, act.username || `#${act.user_id}`);
                      }
                    });
                    return Array.from(seen.entries())
                      .sort((a, b) => a[1].localeCompare(b[1]))
                      .map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ));
                  })()}
                </select>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Usuário</th>
                      <th>Atividade</th>
                      <th>Endpoint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stats?.recent_activities || [])
                      .filter(act => !activityUserFilter || String(act.user_id) === activityUserFilter)
                      .map((act, idx) => (
                      <tr key={idx}>
                        <td className="text-nowrap text-muted" style={{ fontSize: "0.85rem" }}>
                          {act.timestamp
                            ? new Date(act.timestamp).toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit"
                              })
                            : "—"}
                        </td>
                        <td>
                          {act.user_id ? (
                            <Button
                              variant="link"
                              className="p-0 text-primary text-decoration-none"
                              onClick={() => openUserTimeline(act.user_id, act.username)}
                            >
                              {act.username || `#${act.user_id}`}
                            </Button>
                          ) : (
                            <span className="text-muted">{act.username || "—"}</span>
                          )}
                        </td>
                        <td>{act.activity_description || act.activity_type || "—"}</td>
                        <td className="text-muted" style={{ fontSize: "0.8rem" }}>
                          {act.endpoint || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {(!stats?.recent_activities || stats.recent_activities.length === 0) && (
                  <div className="text-center text-muted py-4">
                    Nenhuma atividade recente
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <UserTimelineModal
        show={!!timelineUser}
        onHide={closeTimelineModal}
        userId={timelineUser?.id}
        username={timelineUser?.username}
        token={token}
      />

      {/* Espaçamento extra no final da página */}
      <div style={{ height: "100px" }}></div>
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(UsersDashboard);
